// ============================================================================
// Condition Definitions - Injuries, Diseases, Mental Health
// ============================================================================
import { ConditionCategory, ConditionSeverity, ConditionType, VitalFunction } from './types.js';
// Default values for condition definitions
const CONDITION_DEFAULTS = {
    defaultSeverity: ConditionSeverity.MODERATE,
    affectsWhole: false,
    tendable: false,
    treatable: false,
    operable: false,
    heals: false,
    healRate: 0,
    painOffset: 0,
    bleedRate: 0,
    immunityGainRate: 0,
    lethalSeverity: 0,
    contagious: false,
    contagionRate: 0,
    incubationTicks: 0,
    canBeChronic: false,
    chronicChance: 0,
    effects: [],
    triggers: [],
    incompatible: [],
};
// Helper to create condition definitions with defaults
const createCondition = (partial) => ({
    ...CONDITION_DEFAULTS,
    ...partial,
});
// ============================================================================
// Condition Definitions
// ============================================================================
export const CONDITION_DEFINITIONS = {
    // ===== INJURIES - CUTS =====
    [ConditionType.CUT_MINOR]: createCondition({
        type: ConditionType.CUT_MINOR,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.MINOR,
        tendable: true,
        heals: true,
        healRate: 0.8,
        painOffset: 0.05,
        bleedRate: 0.2,
        canBeChronic: true,
        chronicChance: 0.02,
    }),
    [ConditionType.CUT_DEEP]: createCondition({
        type: ConditionType.CUT_DEEP,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.MODERATE,
        tendable: true,
        heals: true,
        healRate: 0.4,
        painOffset: 0.15,
        bleedRate: 0.6,
        canBeChronic: true,
        chronicChance: 0.1,
        triggers: [ConditionType.INFECTION_MINOR],
    }),
    [ConditionType.LACERATION]: createCondition({
        type: ConditionType.LACERATION,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.MODERATE,
        tendable: true,
        heals: true,
        healRate: 0.3,
        painOffset: 0.2,
        bleedRate: 0.9,
        canBeChronic: true,
        chronicChance: 0.15,
        triggers: [ConditionType.INFECTION_MINOR],
    }),
    // ===== INJURIES - BLUNT =====
    [ConditionType.BRUISE_MINOR]: createCondition({
        type: ConditionType.BRUISE_MINOR,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.NEGLIGIBLE,
        heals: true,
        healRate: 1.0,
        painOffset: 0.03,
    }),
    [ConditionType.BRUISE_MAJOR]: createCondition({
        type: ConditionType.BRUISE_MAJOR,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.MINOR,
        heals: true,
        healRate: 0.6,
        painOffset: 0.1,
    }),
    [ConditionType.INTERNAL_BLEEDING]: createCondition({
        type: ConditionType.INTERNAL_BLEEDING,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.SEVERE,
        operable: true,
        painOffset: 0.2,
        bleedRate: 1.5,
        lethalSeverity: 1.0,
    }),
    [ConditionType.CRUSHED]: createCondition({
        type: ConditionType.CRUSHED,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.CRITICAL,
        operable: true,
        painOffset: 0.6,
        bleedRate: 1.2,
        effects: [
            { function: VitalFunction.MOVEMENT, offset: -0.8 },
            { function: VitalFunction.MANIPULATION, offset: -0.8 },
        ],
    }),
    // ===== INJURIES - FRACTURES =====
    [ConditionType.FRACTURE_HAIRLINE]: createCondition({
        type: ConditionType.FRACTURE_HAIRLINE,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.MINOR,
        tendable: true,
        heals: true,
        healRate: 0.15,
        painOffset: 0.15,
        effects: [{ function: VitalFunction.MOVEMENT, offset: -0.2 }],
    }),
    [ConditionType.FRACTURE_SIMPLE]: createCondition({
        type: ConditionType.FRACTURE_SIMPLE,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.MODERATE,
        tendable: true,
        heals: true,
        healRate: 0.08,
        painOffset: 0.3,
        effects: [{ function: VitalFunction.MOVEMENT, offset: -0.5 }],
    }),
    [ConditionType.FRACTURE_COMPOUND]: createCondition({
        type: ConditionType.FRACTURE_COMPOUND,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.SEVERE,
        operable: true,
        heals: true,
        healRate: 0.03,
        painOffset: 0.5,
        bleedRate: 0.8,
        effects: [{ function: VitalFunction.MOVEMENT, offset: -0.8 }],
        triggers: [ConditionType.INFECTION_MINOR],
    }),
    [ConditionType.DISLOCATION]: createCondition({
        type: ConditionType.DISLOCATION,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.MODERATE,
        tendable: true,
        heals: true,
        healRate: 0.2,
        painOffset: 0.4,
        effects: [
            { function: VitalFunction.MOVEMENT, offset: -0.6 },
            { function: VitalFunction.MANIPULATION, offset: -0.6 },
        ],
    }),
    // ===== INJURIES - BURNS =====
    [ConditionType.BURN_FIRST_DEGREE]: createCondition({
        type: ConditionType.BURN_FIRST_DEGREE,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.MINOR,
        tendable: true,
        heals: true,
        healRate: 0.5,
        painOffset: 0.15,
    }),
    [ConditionType.BURN_SECOND_DEGREE]: createCondition({
        type: ConditionType.BURN_SECOND_DEGREE,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.MODERATE,
        tendable: true,
        heals: true,
        healRate: 0.15,
        painOffset: 0.35,
        canBeChronic: true,
        chronicChance: 0.2,
        triggers: [ConditionType.INFECTION_MINOR],
    }),
    [ConditionType.BURN_THIRD_DEGREE]: createCondition({
        type: ConditionType.BURN_THIRD_DEGREE,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.SEVERE,
        operable: true,
        painOffset: 0.1, // Nerve damage reduces pain
        canBeChronic: true,
        chronicChance: 0.8,
        triggers: [ConditionType.INFECTION_MODERATE],
    }),
    // ===== INJURIES - ANIMAL ATTACKS =====
    [ConditionType.BITE_MINOR]: createCondition({
        type: ConditionType.BITE_MINOR,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.MINOR,
        tendable: true,
        heals: true,
        healRate: 0.4,
        painOffset: 0.1,
        bleedRate: 0.3,
        triggers: [ConditionType.INFECTION_MINOR],
    }),
    [ConditionType.BITE_SEVERE]: createCondition({
        type: ConditionType.BITE_SEVERE,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.SEVERE,
        tendable: true,
        heals: true,
        healRate: 0.15,
        painOffset: 0.35,
        bleedRate: 1.2,
        triggers: [ConditionType.INFECTION_MODERATE],
    }),
    [ConditionType.BITE_VENOMOUS]: createCondition({
        type: ConditionType.BITE_VENOMOUS,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.SEVERE,
        tendable: true,
        treatable: true,
        heals: true,
        healRate: 0.2,
        painOffset: 0.4,
        bleedRate: 0.4,
        triggers: [ConditionType.VENOMED_NEUROTOXIC],
    }),
    [ConditionType.MAULED]: createCondition({
        type: ConditionType.MAULED,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.CRITICAL,
        operable: true,
        painOffset: 0.6,
        bleedRate: 2.0,
        lethalSeverity: 1.0,
        triggers: [ConditionType.INFECTION_SEVERE, ConditionType.PTSD],
    }),
    // ===== DISEASES - INFECTIONS =====
    [ConditionType.INFECTION_MINOR]: createCondition({
        type: ConditionType.INFECTION_MINOR,
        category: ConditionCategory.DISEASE,
        defaultSeverity: ConditionSeverity.MINOR,
        tendable: true,
        treatable: true,
        immunityGainRate: 0.02,
        painOffset: 0.05,
        effects: [{ function: VitalFunction.IMMUNE_FUNCTION, offset: -0.1 }],
        triggers: [ConditionType.INFECTION_MODERATE],
    }),
    [ConditionType.INFECTION_MODERATE]: createCondition({
        type: ConditionType.INFECTION_MODERATE,
        category: ConditionCategory.DISEASE,
        defaultSeverity: ConditionSeverity.MODERATE,
        tendable: true,
        treatable: true,
        immunityGainRate: 0.015,
        painOffset: 0.15,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.1 },
            { function: VitalFunction.IMMUNE_FUNCTION, offset: -0.2 },
        ],
        triggers: [ConditionType.INFECTION_SEVERE],
    }),
    [ConditionType.INFECTION_SEVERE]: createCondition({
        type: ConditionType.INFECTION_SEVERE,
        category: ConditionCategory.DISEASE,
        defaultSeverity: ConditionSeverity.SEVERE,
        treatable: true,
        operable: true,
        immunityGainRate: 0.01,
        painOffset: 0.25,
        lethalSeverity: 1.0,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.25 },
            { function: VitalFunction.IMMUNE_FUNCTION, offset: -0.4 },
        ],
        triggers: [ConditionType.SEPSIS],
    }),
    [ConditionType.SEPSIS]: createCondition({
        type: ConditionType.SEPSIS,
        category: ConditionCategory.DISEASE,
        defaultSeverity: ConditionSeverity.CRITICAL,
        affectsWhole: true,
        treatable: true,
        painOffset: 0.3,
        lethalSeverity: 1.0,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.4 },
            { function: VitalFunction.BLOOD_PUMPING, offset: -0.3 },
        ],
        triggers: [ConditionType.SEPTIC_SHOCK],
    }),
    [ConditionType.SEPTIC_SHOCK]: createCondition({
        type: ConditionType.SEPTIC_SHOCK,
        category: ConditionCategory.DISEASE,
        defaultSeverity: ConditionSeverity.FATAL,
        affectsWhole: true,
        treatable: true,
        lethalSeverity: 0.8,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.7 },
            { function: VitalFunction.BLOOD_PUMPING, offset: -0.6 },
            { function: VitalFunction.BREATHING, offset: -0.4 },
        ],
        triggers: [ConditionType.MULTI_ORGAN_FAILURE],
    }),
    [ConditionType.GANGRENE]: createCondition({
        type: ConditionType.GANGRENE,
        category: ConditionCategory.DISEASE,
        defaultSeverity: ConditionSeverity.SEVERE,
        operable: true,
        lethalSeverity: 1.0,
        painOffset: 0.4,
        triggers: [ConditionType.SEPSIS],
    }),
    // ===== DISEASES - VIRAL =====
    [ConditionType.FLU]: createCondition({
        type: ConditionType.FLU,
        category: ConditionCategory.DISEASE,
        defaultSeverity: ConditionSeverity.MINOR,
        affectsWhole: true,
        contagious: true,
        contagionRate: 0.3,
        incubationTicks: 1000,
        immunityGainRate: 0.025,
        painOffset: 0.1,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.1 },
            { function: VitalFunction.BREATHING, offset: -0.15 },
        ],
    }),
    [ConditionType.COMMON_COLD]: createCondition({
        type: ConditionType.COMMON_COLD,
        category: ConditionCategory.DISEASE,
        defaultSeverity: ConditionSeverity.NEGLIGIBLE,
        affectsWhole: true,
        contagious: true,
        contagionRate: 0.4,
        incubationTicks: 500,
        immunityGainRate: 0.04,
        effects: [{ function: VitalFunction.BREATHING, offset: -0.05 }],
    }),
    [ConditionType.PNEUMONIA_VIRAL]: createCondition({
        type: ConditionType.PNEUMONIA_VIRAL,
        category: ConditionCategory.DISEASE,
        defaultSeverity: ConditionSeverity.SEVERE,
        affectsWhole: true,
        treatable: true,
        contagious: true,
        contagionRate: 0.2,
        immunityGainRate: 0.01,
        lethalSeverity: 1.0,
        effects: [
            { function: VitalFunction.BREATHING, offset: -0.5 },
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.2 },
        ],
    }),
    // ===== DISEASES - BLOOD =====
    [ConditionType.BLOOD_LOSS_MINOR]: createCondition({
        type: ConditionType.BLOOD_LOSS_MINOR,
        category: ConditionCategory.CIRCULATORY,
        defaultSeverity: ConditionSeverity.MINOR,
        affectsWhole: true,
        heals: true,
        healRate: 0.1,
        effects: [{ function: VitalFunction.CONSCIOUSNESS, offset: -0.1 }],
    }),
    [ConditionType.BLOOD_LOSS_MODERATE]: createCondition({
        type: ConditionType.BLOOD_LOSS_MODERATE,
        category: ConditionCategory.CIRCULATORY,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        heals: true,
        healRate: 0.05,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.25 },
            { function: VitalFunction.MOVEMENT, offset: -0.2 },
        ],
    }),
    [ConditionType.BLOOD_LOSS_SEVERE]: createCondition({
        type: ConditionType.BLOOD_LOSS_SEVERE,
        category: ConditionCategory.CIRCULATORY,
        defaultSeverity: ConditionSeverity.SEVERE,
        affectsWhole: true,
        heals: true,
        healRate: 0.02,
        lethalSeverity: 1.0,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.5 },
            { function: VitalFunction.MOVEMENT, offset: -0.5 },
        ],
    }),
    [ConditionType.BLOOD_LOSS_CRITICAL]: createCondition({
        type: ConditionType.BLOOD_LOSS_CRITICAL,
        category: ConditionCategory.CIRCULATORY,
        defaultSeverity: ConditionSeverity.CRITICAL,
        affectsWhole: true,
        lethalSeverity: 0.9,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.8 },
            { function: VitalFunction.MOVEMENT, offset: -0.9 },
        ],
    }),
    [ConditionType.ANEMIA]: createCondition({
        type: ConditionType.ANEMIA,
        category: ConditionCategory.CIRCULATORY,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        treatable: true,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.15 },
            { function: VitalFunction.MOVEMENT, offset: -0.2 },
        ],
    }),
    [ConditionType.HEART_ATTACK]: createCondition({
        type: ConditionType.HEART_ATTACK,
        category: ConditionCategory.CIRCULATORY,
        defaultSeverity: ConditionSeverity.EXTREME,
        affectsWhole: true,
        treatable: true,
        painOffset: 0.8,
        lethalSeverity: 0.7,
        effects: [
            { function: VitalFunction.BLOOD_PUMPING, offset: -0.8 },
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.5 },
        ],
        triggers: [ConditionType.HEART_FAILURE],
    }),
    [ConditionType.STROKE]: createCondition({
        type: ConditionType.STROKE,
        category: ConditionCategory.CIRCULATORY,
        defaultSeverity: ConditionSeverity.EXTREME,
        affectsWhole: true,
        treatable: true,
        lethalSeverity: 0.6,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.6 },
            { function: VitalFunction.MOVEMENT, offset: -0.7 },
            { function: VitalFunction.TALKING, offset: -0.5 },
        ],
        triggers: [ConditionType.PARALYSIS_PARTIAL],
    }),
    // ===== MENTAL - ACUTE =====
    [ConditionType.STRESSED]: createCondition({
        type: ConditionType.STRESSED,
        category: ConditionCategory.MENTAL_ACUTE,
        defaultSeverity: ConditionSeverity.MINOR,
        affectsWhole: true,
        heals: true,
        healRate: 0.3,
        triggers: [ConditionType.ANXIOUS],
    }),
    [ConditionType.ANXIOUS]: createCondition({
        type: ConditionType.ANXIOUS,
        category: ConditionCategory.MENTAL_ACUTE,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        heals: true,
        healRate: 0.15,
        effects: [{ function: VitalFunction.CONSCIOUSNESS, offset: -0.05 }],
        triggers: [ConditionType.PANIC_ATTACK],
    }),
    [ConditionType.PANIC_ATTACK]: createCondition({
        type: ConditionType.PANIC_ATTACK,
        category: ConditionCategory.MENTAL_ACUTE,
        defaultSeverity: ConditionSeverity.SEVERE,
        affectsWhole: true,
        heals: true,
        healRate: 1.0, // Passes quickly
        painOffset: 0.2,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.2 },
            { function: VitalFunction.BREATHING, offset: -0.3 },
        ],
    }),
    [ConditionType.DEPRESSIVE_EPISODE]: createCondition({
        type: ConditionType.DEPRESSIVE_EPISODE,
        category: ConditionCategory.MENTAL_ACUTE,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        treatable: true,
        heals: true,
        healRate: 0.05,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.15 },
            { function: VitalFunction.MOVEMENT, offset: -0.2 },
        ],
        canBeChronic: true,
        chronicChance: 0.2,
    }),
    [ConditionType.BERSERK]: createCondition({
        type: ConditionType.BERSERK,
        category: ConditionCategory.MENTAL_ACUTE,
        defaultSeverity: ConditionSeverity.SEVERE,
        affectsWhole: true,
        heals: true,
        healRate: 0.2,
        painOffset: -0.5, // Reduced pain perception
        effects: [{ function: VitalFunction.CONSCIOUSNESS, offset: -0.3 }],
    }),
    [ConditionType.FLASHBACK]: createCondition({
        type: ConditionType.FLASHBACK,
        category: ConditionCategory.MENTAL_ACUTE,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        heals: true,
        healRate: 0.5,
        effects: [{ function: VitalFunction.CONSCIOUSNESS, offset: -0.3 }],
    }),
    [ConditionType.DISSOCIATIVE_EPISODE]: createCondition({
        type: ConditionType.DISSOCIATIVE_EPISODE,
        category: ConditionCategory.MENTAL_ACUTE,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        heals: true,
        healRate: 0.3,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.2 },
            { function: VitalFunction.PAIN_SENSATION, offset: -0.5 },
        ],
    }),
    // ===== MENTAL - CHRONIC =====
    [ConditionType.PTSD]: createCondition({
        type: ConditionType.PTSD,
        category: ConditionCategory.MENTAL_CHRONIC,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        treatable: true,
        effects: [{ function: VitalFunction.CONSCIOUSNESS, offset: -0.1 }],
        triggers: [ConditionType.FLASHBACK, ConditionType.PANIC_ATTACK],
    }),
    [ConditionType.COMPLEX_PTSD]: createCondition({
        type: ConditionType.COMPLEX_PTSD,
        category: ConditionCategory.MENTAL_CHRONIC,
        defaultSeverity: ConditionSeverity.SEVERE,
        affectsWhole: true,
        treatable: true,
        effects: [{ function: VitalFunction.CONSCIOUSNESS, offset: -0.2 }],
        triggers: [ConditionType.FLASHBACK, ConditionType.DISSOCIATIVE_EPISODE],
    }),
    [ConditionType.GENERALIZED_ANXIETY]: createCondition({
        type: ConditionType.GENERALIZED_ANXIETY,
        category: ConditionCategory.MENTAL_CHRONIC,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        treatable: true,
        effects: [{ function: VitalFunction.CONSCIOUSNESS, offset: -0.1 }],
        triggers: [ConditionType.PANIC_ATTACK],
    }),
    [ConditionType.SOCIAL_ANXIETY]: createCondition({
        type: ConditionType.SOCIAL_ANXIETY,
        category: ConditionCategory.MENTAL_CHRONIC,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        treatable: true,
        effects: [{ function: VitalFunction.TALKING, offset: -0.3 }],
    }),
    [ConditionType.PANIC_DISORDER]: createCondition({
        type: ConditionType.PANIC_DISORDER,
        category: ConditionCategory.MENTAL_CHRONIC,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        treatable: true,
        triggers: [ConditionType.PANIC_ATTACK],
    }),
    [ConditionType.AGORAPHOBIA]: createCondition({
        type: ConditionType.AGORAPHOBIA,
        category: ConditionCategory.MENTAL_CHRONIC,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        treatable: true,
        effects: [{ function: VitalFunction.MOVEMENT, offset: -0.3 }],
        triggers: [ConditionType.PANIC_ATTACK],
    }),
    [ConditionType.CLAUSTROPHOBIA]: createCondition({
        type: ConditionType.CLAUSTROPHOBIA,
        category: ConditionCategory.MENTAL_CHRONIC,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        treatable: true,
        triggers: [ConditionType.PANIC_ATTACK],
    }),
    [ConditionType.OCD]: createCondition({
        type: ConditionType.OCD,
        category: ConditionCategory.MENTAL_CHRONIC,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        treatable: true,
        effects: [{ function: VitalFunction.CONSCIOUSNESS, offset: -0.1 }],
    }),
    [ConditionType.MAJOR_DEPRESSION]: createCondition({
        type: ConditionType.MAJOR_DEPRESSION,
        category: ConditionCategory.MENTAL_CHRONIC,
        defaultSeverity: ConditionSeverity.SEVERE,
        affectsWhole: true,
        treatable: true,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.2 },
            { function: VitalFunction.MOVEMENT, offset: -0.3 },
        ],
        triggers: [ConditionType.SUICIDAL_IDEATION],
    }),
    [ConditionType.BIPOLAR_TYPE_1]: createCondition({
        type: ConditionType.BIPOLAR_TYPE_1,
        category: ConditionCategory.MENTAL_CHRONIC,
        defaultSeverity: ConditionSeverity.SEVERE,
        affectsWhole: true,
        treatable: true,
        triggers: [ConditionType.MANIC_EPISODE, ConditionType.DEPRESSIVE_EPISODE],
    }),
    [ConditionType.SCHIZOPHRENIA]: createCondition({
        type: ConditionType.SCHIZOPHRENIA,
        category: ConditionCategory.MENTAL_CHRONIC,
        defaultSeverity: ConditionSeverity.SEVERE,
        affectsWhole: true,
        treatable: true,
        effects: [{ function: VitalFunction.CONSCIOUSNESS, offset: -0.3 }],
        triggers: [ConditionType.PSYCHOTIC_BREAK],
    }),
    [ConditionType.AUTISM_SPECTRUM]: createCondition({
        type: ConditionType.AUTISM_SPECTRUM,
        category: ConditionCategory.DEVELOPMENTAL,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        effects: [{ function: VitalFunction.TALKING, offset: -0.2 }],
    }),
    [ConditionType.ADHD]: createCondition({
        type: ConditionType.ADHD,
        category: ConditionCategory.DEVELOPMENTAL,
        defaultSeverity: ConditionSeverity.MINOR,
        affectsWhole: true,
        treatable: true,
        effects: [{ function: VitalFunction.CONSCIOUSNESS, offset: -0.1 }],
    }),
    // ===== SENSORY =====
    [ConditionType.BLINDNESS_PARTIAL]: createCondition({
        type: ConditionType.BLINDNESS_PARTIAL,
        category: ConditionCategory.SENSORY,
        defaultSeverity: ConditionSeverity.MODERATE,
        effects: [{ function: VitalFunction.SIGHT, offset: -0.5 }],
    }),
    [ConditionType.BLINDNESS_COMPLETE]: createCondition({
        type: ConditionType.BLINDNESS_COMPLETE,
        category: ConditionCategory.SENSORY,
        defaultSeverity: ConditionSeverity.SEVERE,
        effects: [{ function: VitalFunction.SIGHT, offset: -1.0 }],
    }),
    [ConditionType.DEAFNESS_PARTIAL]: createCondition({
        type: ConditionType.DEAFNESS_PARTIAL,
        category: ConditionCategory.SENSORY,
        defaultSeverity: ConditionSeverity.MODERATE,
        effects: [{ function: VitalFunction.HEARING, offset: -0.5 }],
    }),
    [ConditionType.DEAFNESS_COMPLETE]: createCondition({
        type: ConditionType.DEAFNESS_COMPLETE,
        category: ConditionCategory.SENSORY,
        defaultSeverity: ConditionSeverity.SEVERE,
        effects: [{ function: VitalFunction.HEARING, offset: -1.0 }],
    }),
    [ConditionType.TINNITUS]: createCondition({
        type: ConditionType.TINNITUS,
        category: ConditionCategory.SENSORY,
        defaultSeverity: ConditionSeverity.MINOR,
        painOffset: 0.05,
        effects: [{ function: VitalFunction.HEARING, offset: -0.1 }],
    }),
    [ConditionType.HYPERACUSIS]: createCondition({
        type: ConditionType.HYPERACUSIS,
        category: ConditionCategory.SENSORY,
        defaultSeverity: ConditionSeverity.MODERATE,
        painOffset: 0.15,
        effects: [{ function: VitalFunction.HEARING, offset: 0.3 }], // Too sensitive
        triggers: [ConditionType.PANIC_ATTACK],
    }),
    [ConditionType.MISOPHONIA]: createCondition({
        type: ConditionType.MISOPHONIA,
        category: ConditionCategory.SENSORY,
        defaultSeverity: ConditionSeverity.MODERATE,
        triggers: [ConditionType.BERSERK, ConditionType.ANXIOUS],
    }),
    [ConditionType.PHOTOPHOBIA]: createCondition({
        type: ConditionType.PHOTOPHOBIA,
        category: ConditionCategory.SENSORY,
        defaultSeverity: ConditionSeverity.MODERATE,
        painOffset: 0.1,
        effects: [{ function: VitalFunction.SIGHT, offset: -0.2 }],
    }),
    [ConditionType.PHANTOM_PAIN]: createCondition({
        type: ConditionType.PHANTOM_PAIN,
        category: ConditionCategory.SENSORY,
        defaultSeverity: ConditionSeverity.MODERATE,
        painOffset: 0.3,
        heals: true,
        healRate: 0.01,
    }),
    [ConditionType.ALLODYNIA]: createCondition({
        type: ConditionType.ALLODYNIA,
        category: ConditionCategory.SENSORY,
        defaultSeverity: ConditionSeverity.MODERATE,
        painOffset: 0.4,
        effects: [{ function: VitalFunction.PAIN_SENSATION, offset: 0.5 }],
    }),
    [ConditionType.TACTILE_DEFENSIVENESS]: createCondition({
        type: ConditionType.TACTILE_DEFENSIVENESS,
        category: ConditionCategory.SENSORY,
        defaultSeverity: ConditionSeverity.MINOR,
        effects: [{ function: VitalFunction.PAIN_SENSATION, offset: 0.2 }],
        triggers: [ConditionType.ANXIOUS],
    }),
    // ===== ENVIRONMENTAL =====
    [ConditionType.HYPOTHERMIA_MILD]: createCondition({
        type: ConditionType.HYPOTHERMIA_MILD,
        category: ConditionCategory.ENVIRONMENTAL,
        defaultSeverity: ConditionSeverity.MINOR,
        affectsWhole: true,
        heals: true,
        healRate: 0.5,
        effects: [
            { function: VitalFunction.MOVEMENT, offset: -0.1 },
            { function: VitalFunction.FINE_MOTOR, offset: -0.2 },
        ],
        triggers: [ConditionType.HYPOTHERMIA_MODERATE],
    }),
    [ConditionType.HYPOTHERMIA_MODERATE]: createCondition({
        type: ConditionType.HYPOTHERMIA_MODERATE,
        category: ConditionCategory.ENVIRONMENTAL,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        heals: true,
        healRate: 0.2,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.2 },
            { function: VitalFunction.MOVEMENT, offset: -0.3 },
        ],
        triggers: [ConditionType.HYPOTHERMIA_SEVERE],
    }),
    [ConditionType.HYPOTHERMIA_SEVERE]: createCondition({
        type: ConditionType.HYPOTHERMIA_SEVERE,
        category: ConditionCategory.ENVIRONMENTAL,
        defaultSeverity: ConditionSeverity.CRITICAL,
        affectsWhole: true,
        lethalSeverity: 1.0,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.5 },
            { function: VitalFunction.BLOOD_PUMPING, offset: -0.4 },
        ],
    }),
    [ConditionType.HEAT_STROKE]: createCondition({
        type: ConditionType.HEAT_STROKE,
        category: ConditionCategory.ENVIRONMENTAL,
        defaultSeverity: ConditionSeverity.CRITICAL,
        affectsWhole: true,
        lethalSeverity: 1.0,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.6 },
            { function: VitalFunction.TEMPERATURE_REGULATION, offset: -0.8 },
        ],
    }),
    [ConditionType.EXHAUSTED]: createCondition({
        type: ConditionType.EXHAUSTED,
        category: ConditionCategory.ENVIRONMENTAL,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        heals: true,
        healRate: 0.3,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.3 },
            { function: VitalFunction.MOVEMENT, offset: -0.4 },
        ],
    }),
    [ConditionType.STARVING_SEVERE]: createCondition({
        type: ConditionType.STARVING_SEVERE,
        category: ConditionCategory.ENVIRONMENTAL,
        defaultSeverity: ConditionSeverity.SEVERE,
        affectsWhole: true,
        lethalSeverity: 1.0,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.4 },
            { function: VitalFunction.MOVEMENT, offset: -0.6 },
            { function: VitalFunction.IMMUNE_FUNCTION, offset: -0.5 },
        ],
    }),
    // ===== POISON =====
    [ConditionType.VENOMED_NEUROTOXIC]: createCondition({
        type: ConditionType.VENOMED_NEUROTOXIC,
        category: ConditionCategory.POISON,
        defaultSeverity: ConditionSeverity.SEVERE,
        affectsWhole: true,
        treatable: true,
        immunityGainRate: 0.005,
        painOffset: 0.3,
        lethalSeverity: 1.0,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.4 },
            { function: VitalFunction.BREATHING, offset: -0.3 },
            { function: VitalFunction.MOVEMENT, offset: -0.5 },
        ],
        triggers: [ConditionType.PARALYSIS_PARTIAL],
    }),
    [ConditionType.PARALYSIS_PARTIAL]: createCondition({
        type: ConditionType.PARALYSIS_PARTIAL,
        category: ConditionCategory.POISON,
        defaultSeverity: ConditionSeverity.SEVERE,
        heals: true,
        healRate: 0.1,
        effects: [
            { function: VitalFunction.MOVEMENT, offset: -0.7 },
            { function: VitalFunction.MANIPULATION, offset: -0.7 },
        ],
        triggers: [ConditionType.PARALYSIS_COMPLETE],
    }),
    [ConditionType.PARALYSIS_COMPLETE]: createCondition({
        type: ConditionType.PARALYSIS_COMPLETE,
        category: ConditionCategory.POISON,
        defaultSeverity: ConditionSeverity.CRITICAL,
        heals: true,
        healRate: 0.05,
        effects: [
            { function: VitalFunction.MOVEMENT, offset: -1.0 },
            { function: VitalFunction.MANIPULATION, offset: -1.0 },
        ],
        triggers: [ConditionType.RESPIRATORY_PARALYSIS],
    }),
    [ConditionType.RESPIRATORY_PARALYSIS]: createCondition({
        type: ConditionType.RESPIRATORY_PARALYSIS,
        category: ConditionCategory.POISON,
        defaultSeverity: ConditionSeverity.FATAL,
        lethalSeverity: 0.9,
        effects: [{ function: VitalFunction.BREATHING, offset: -0.9 }],
    }),
    [ConditionType.ANAPHYLAXIS]: createCondition({
        type: ConditionType.ANAPHYLAXIS,
        category: ConditionCategory.POISON,
        defaultSeverity: ConditionSeverity.EXTREME,
        affectsWhole: true,
        treatable: true,
        lethalSeverity: 0.8,
        effects: [
            { function: VitalFunction.BREATHING, offset: -0.7 },
            { function: VitalFunction.BLOOD_PUMPING, offset: -0.5 },
        ],
    }),
    // ===== ADDICTION =====
    [ConditionType.ADDICTION_ALCOHOL]: createCondition({
        type: ConditionType.ADDICTION_ALCOHOL,
        category: ConditionCategory.ADDICTION,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        treatable: true,
        triggers: [ConditionType.WITHDRAWAL_MODERATE, ConditionType.CRAVING],
    }),
    [ConditionType.WITHDRAWAL_MILD]: createCondition({
        type: ConditionType.WITHDRAWAL_MILD,
        category: ConditionCategory.ADDICTION,
        defaultSeverity: ConditionSeverity.MINOR,
        affectsWhole: true,
        heals: true,
        healRate: 0.1,
        painOffset: 0.1,
        effects: [{ function: VitalFunction.CONSCIOUSNESS, offset: -0.1 }],
        triggers: [ConditionType.ANXIOUS],
    }),
    [ConditionType.WITHDRAWAL_SEVERE]: createCondition({
        type: ConditionType.WITHDRAWAL_SEVERE,
        category: ConditionCategory.ADDICTION,
        defaultSeverity: ConditionSeverity.SEVERE,
        affectsWhole: true,
        heals: true,
        healRate: 0.03,
        painOffset: 0.4,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.3 },
            { function: VitalFunction.MOVEMENT, offset: -0.3 },
        ],
        triggers: [ConditionType.SEIZURE, ConditionType.PSYCHOTIC_BREAK],
    }),
    [ConditionType.CRAVING]: createCondition({
        type: ConditionType.CRAVING,
        category: ConditionCategory.ADDICTION,
        defaultSeverity: ConditionSeverity.MINOR,
        affectsWhole: true,
        heals: true,
        healRate: 0.2,
        effects: [{ function: VitalFunction.CONSCIOUSNESS, offset: -0.1 }],
    }),
    // ===== NEUROLOGICAL =====
    [ConditionType.SEIZURE]: createCondition({
        type: ConditionType.SEIZURE,
        category: ConditionCategory.NEUROLOGICAL,
        defaultSeverity: ConditionSeverity.SEVERE,
        affectsWhole: true,
        heals: true,
        healRate: 0.8,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.8 },
            { function: VitalFunction.MOVEMENT, offset: -0.9 },
        ],
    }),
    [ConditionType.MIGRAINE]: createCondition({
        type: ConditionType.MIGRAINE,
        category: ConditionCategory.NEUROLOGICAL,
        defaultSeverity: ConditionSeverity.MODERATE,
        heals: true,
        healRate: 0.15,
        painOffset: 0.5,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.3 },
            { function: VitalFunction.SIGHT, offset: -0.3 },
        ],
        triggers: [ConditionType.PHOTOPHOBIA],
    }),
    [ConditionType.VERTIGO]: createCondition({
        type: ConditionType.VERTIGO,
        category: ConditionCategory.NEUROLOGICAL,
        defaultSeverity: ConditionSeverity.MODERATE,
        heals: true,
        healRate: 0.3,
        effects: [
            { function: VitalFunction.BALANCE, offset: -0.7 },
            { function: VitalFunction.MOVEMENT, offset: -0.4 },
        ],
    }),
    [ConditionType.CONCUSSION]: createCondition({
        type: ConditionType.CONCUSSION,
        category: ConditionCategory.NEUROLOGICAL,
        defaultSeverity: ConditionSeverity.MODERATE,
        heals: true,
        healRate: 0.05,
        painOffset: 0.2,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.3 },
            { function: VitalFunction.BALANCE, offset: -0.3 },
        ],
        canBeChronic: true,
        chronicChance: 0.1,
        triggers: [ConditionType.POST_CONCUSSION_SYNDROME],
    }),
    [ConditionType.TRAUMATIC_BRAIN_INJURY]: createCondition({
        type: ConditionType.TRAUMATIC_BRAIN_INJURY,
        category: ConditionCategory.NEUROLOGICAL,
        defaultSeverity: ConditionSeverity.CRITICAL,
        operable: true,
        lethalSeverity: 0.8,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.6 },
            { function: VitalFunction.MOVEMENT, offset: -0.4 },
            { function: VitalFunction.TALKING, offset: -0.3 },
        ],
        triggers: [ConditionType.DEMENTIA_EARLY, ConditionType.PTSD],
    }),
    [ConditionType.DEMENTIA_EARLY]: createCondition({
        type: ConditionType.DEMENTIA_EARLY,
        category: ConditionCategory.NEUROLOGICAL,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        effects: [{ function: VitalFunction.CONSCIOUSNESS, offset: -0.2 }],
        triggers: [ConditionType.DEMENTIA_MODERATE],
    }),
    [ConditionType.DEMENTIA_SEVERE]: createCondition({
        type: ConditionType.DEMENTIA_SEVERE,
        category: ConditionCategory.NEUROLOGICAL,
        defaultSeverity: ConditionSeverity.CRITICAL,
        affectsWhole: true,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.6 },
            { function: VitalFunction.MOVEMENT, offset: -0.4 },
            { function: VitalFunction.TALKING, offset: -0.5 },
        ],
    }),
    [ConditionType.NERVE_DAMAGE]: createCondition({
        type: ConditionType.NERVE_DAMAGE,
        category: ConditionCategory.NEUROLOGICAL,
        defaultSeverity: ConditionSeverity.MODERATE,
        operable: true,
        painOffset: 0.2,
        effects: [
            { function: VitalFunction.PAIN_SENSATION, offset: -0.5 },
            { function: VitalFunction.FINE_MOTOR, offset: -0.4 },
        ],
        canBeChronic: true,
        chronicChance: 0.5,
        triggers: [ConditionType.PHANTOM_PAIN, ConditionType.NUMBNESS_PARTIAL],
    }),
    // ===== CHRONIC PHYSICAL =====
    [ConditionType.CHRONIC_PAIN]: createCondition({
        type: ConditionType.CHRONIC_PAIN,
        category: ConditionCategory.CHRONIC,
        defaultSeverity: ConditionSeverity.MODERATE,
        treatable: true,
        painOffset: 0.3,
        effects: [{ function: VitalFunction.CONSCIOUSNESS, offset: -0.15 }],
        triggers: [ConditionType.DEPRESSIVE_EPISODE],
    }),
    [ConditionType.FIBROMYALGIA]: createCondition({
        type: ConditionType.FIBROMYALGIA,
        category: ConditionCategory.CHRONIC,
        defaultSeverity: ConditionSeverity.MODERATE,
        affectsWhole: true,
        treatable: true,
        painOffset: 0.35,
        effects: [
            { function: VitalFunction.MOVEMENT, offset: -0.2 },
            { function: VitalFunction.CONSCIOUSNESS, offset: -0.15 },
        ],
    }),
    [ConditionType.ARTHRITIS]: createCondition({
        type: ConditionType.ARTHRITIS,
        category: ConditionCategory.CHRONIC,
        defaultSeverity: ConditionSeverity.MODERATE,
        treatable: true,
        painOffset: 0.2,
        effects: [
            { function: VitalFunction.MOVEMENT, offset: -0.3 },
            { function: VitalFunction.MANIPULATION, offset: -0.3 },
        ],
    }),
    // ===== SPECIAL STATES =====
    [ConditionType.UNCONSCIOUS]: createCondition({
        type: ConditionType.UNCONSCIOUS,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.SEVERE,
        affectsWhole: true,
        heals: true,
        healRate: 0.5,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -1.0 },
            { function: VitalFunction.MOVEMENT, offset: -1.0 },
        ],
    }),
    [ConditionType.COMA]: createCondition({
        type: ConditionType.COMA,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.CRITICAL,
        affectsWhole: true,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -1.0 },
            { function: VitalFunction.MOVEMENT, offset: -1.0 },
            { function: VitalFunction.TALKING, offset: -1.0 },
        ],
    }),
    [ConditionType.DEAD]: createCondition({
        type: ConditionType.DEAD,
        category: ConditionCategory.INJURY,
        defaultSeverity: ConditionSeverity.FATAL,
        affectsWhole: true,
        lethalSeverity: 0,
        effects: [
            { function: VitalFunction.CONSCIOUSNESS, offset: -1.0 },
            { function: VitalFunction.BLOOD_PUMPING, offset: -1.0 },
            { function: VitalFunction.BREATHING, offset: -1.0 },
        ],
    }),
};
// ============================================================================
// Helper Functions
// ============================================================================
export const getConditionDefinition = ({ type, }) => {
    return CONDITION_DEFINITIONS[type];
};
export const isConditionLethal = ({ type }) => {
    const def = CONDITION_DEFINITIONS[type];
    return def !== undefined && def.lethalSeverity > 0;
};
export const isConditionContagious = ({ type }) => {
    const def = CONDITION_DEFINITIONS[type];
    return def?.contagious === true;
};
export const getConditionsByCategory = ({ category, }) => {
    const results = [];
    for (const [typeStr, def] of Object.entries(CONDITION_DEFINITIONS)) {
        if (def && def.category === category) {
            results.push(Number(typeStr));
        }
    }
    return results;
};
export const getMentalConditions = () => {
    const results = [];
    for (const [typeStr, def] of Object.entries(CONDITION_DEFINITIONS)) {
        if (def &&
            (def.category === ConditionCategory.MENTAL_ACUTE ||
                def.category === ConditionCategory.MENTAL_CHRONIC)) {
            results.push(Number(typeStr));
        }
    }
    return results;
};
//# sourceMappingURL=conditions.js.map